import ImageProps, { ColorFilterProps, ExtendPadding, LayerAreaSelection, LayerProps, OverlayRect, Point } from "../../types/imageStudio/images";
import { CanvasDisplayRectProps } from "../../types/imageStudio/layerComponent";
import { visualPaddingToLocalPadding } from "../../types/imageStudio/overlay";
import { canvasToFile, loadImage } from "./imagesFunctions";

export const getLayerDisplayStyle = (layer: LayerProps, canvasRef: any, canvasDisplayRect: CanvasDisplayRectProps | null ) => {
    const canvas = canvasRef?.current as HTMLCanvasElement | null;
    if (!canvas || !canvasDisplayRect) return null;

    const scaleX = canvasDisplayRect.width / canvas.width;
    const scaleY = canvasDisplayRect.height / canvas.height;

    return {
        left: canvasDisplayRect.left + layer.position.x * scaleX,
        top: canvasDisplayRect.top + layer.position.y * scaleY,
        width: layer.width * scaleX,
        height: layer.height * scaleY
    };
};
//text layer functions
export const parseFontSizePx = (fontSize?: string) => {
    const n = Number((fontSize ?? "24").replace("px", "").trim());
    return Number.isFinite(n) ? n : 24;
};
const autoFitFontSize = (text: string, width: number, height: number) => {
    if (!text?.trim()) return 12;

    const lines = text.split("\n");
    const longest = lines.reduce((max, line) => Math.max(max, line.length), 1);

    const widthBased = width / Math.max(longest * 0.62, 1);
    const heightBased = height / Math.max(lines.length * 1.35, 1);

    return Math.max(8, Math.floor(Math.min(widthBased, heightBased)));
};

//generall layer functions
export const applyLayerResizeContentSync = (
    oldLayer: LayerProps,
    resizedLayer: LayerProps
): LayerProps => {
    const oldW = Math.max(1, oldLayer.width);
    const oldH = Math.max(1, oldLayer.height);
    const scaleX = resizedLayer.width / oldW;
    const scaleY = resizedLayer.height / oldH;

    if (oldLayer.type === "drawings") {
        return {
            ...resizedLayer,
            drawingData: {
                ...oldLayer.drawingData,
                strokes: []
            }
        };
    }

    if (oldLayer.type === "text" && oldLayer.textData) {
        const nextFontSize = autoFitFontSize(
            oldLayer.textData.text,
            resizedLayer.width,
            resizedLayer.height
        );

        return {
            ...resizedLayer,
            textData: {
                ...oldLayer.textData,
                fontSize: `${nextFontSize}px`,
                autoFit: true
            }
        };
    }

    return resizedLayer;
};

export const getSelectionRectForLayer = (selection: LayerAreaSelection, layer: LayerProps) => ({
    x: Math.max(0, Math.round(selection.x - layer.position.x)),
    y: Math.max(0, Math.round(selection.y - layer.position.y)),
    w: Math.max(1, Math.round(selection.w)),
    h: Math.max(1, Math.round(selection.h))
});

export const getLayerRasterSource = (layer: LayerProps) => {
    if (layer.type === "drawings") {
        return layer.drawingData?.rasterSrc || null;
    }

    return layer.imageEdit?.src || layer.imageData?.src || null;
};

export const cloneLayer = (layer: LayerProps): LayerProps => ({
    ...layer,
    position: { ...layer.position },
    textData: layer.textData
        ? {
            ...layer.textData,
            style: [...(layer.textData.style ?? [])]
        }
        : undefined,
    drawingData: layer.drawingData
        ? {
            ...layer.drawingData,
            strokes: (layer.drawingData.strokes ?? []).map((stroke) => ({
                ...stroke,
                points: stroke.points.map((p) => ({ ...p }))
            }))
        }
        : undefined,
    imageData: layer.imageData ? { ...layer.imageData } : undefined,
    imageEdit: layer.imageEdit ? { ...layer.imageEdit } : undefined,
    imageLayerStyle: layer.imageLayerStyle
        ? { ...layer.imageLayerStyle }
        : undefined,
    history: undefined
});

export const normalizeRect = (x1: number, y1: number, x2: number, y2: number) => ({
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    w: Math.abs(x2 - x1),
    h: Math.abs(y2 - y1)
});

export const rectsIntersect = (a: OverlayRect,b: OverlayRect) => {
    return !(
        a.x + a.w < b.x ||
        b.x + b.w < a.x ||
        a.y + a.h < b.y ||
        b.y + b.h < a.y
    );
};

export const isPointInsideSelectionShape = (
    point: Point,
    selection: LayerAreaSelection
) => {
    if (selection.mode === "rect") {
        return (
            point.x >= selection.x &&
            point.x <= selection.x + selection.w &&
            point.y >= selection.y &&
            point.y <= selection.y + selection.h
        );
    }

    if (selection.mode === "oval") {
        return isPointInEllipse(point, {
            x: selection.x,
            y: selection.y,
            w: selection.w,
            h: selection.h
        });
    }

    if (selection.mode === "free") {
        return isPointInPolygon(point, selection.points);
    }

    return false;
};

export const closeFreePath = (points: Point[]) => {
    if (points.length < 3) return points;
    const first = points[0];
    const last = points[points.length - 1];

    if (first.x === last.x && first.y === last.y) return points;
    return [...points, first];
};

export const isPointInsideLayer = (point: Point, layer: LayerProps) => {
    return (
        point.x >= layer.position.x &&
        point.x <= layer.position.x + layer.width &&
        point.y >= layer.position.y &&
        point.y <= layer.position.y + layer.height
    );
};

export const getTopMostLayerAtPoint = (imageLayer: LayerProps[], point: Point) => {
    //avoid selecting hidden layer
    const sorted = [...imageLayer]
        .filter((layer) => !layer.hidden)
        .sort((a, b) => Number(b.orderNumber) - Number(a.orderNumber));

    return sorted.find((layer) => isPointInsideLayer(point, layer)) ?? null;
};

export const clampPointToLayer = (point: Point, layer: LayerProps): Point => ({
    x: Math.max(layer.position.x, Math.min(layer.position.x + layer.width, point.x)),
    y: Math.max(layer.position.y, Math.min(layer.position.y + layer.height, point.y))
});

export const rebuildOrderNumbers = (layers: LayerProps[]) => {
    return layers.map((layer, index) => ({
        ...layer,
        orderNumber: String(index + 1)
    }));
};

export const cropImageLayerDataUrl = async (
    src: string,
    crop: { x: number; y: number; w: number; h: number },
    layerWidth: number,
    layerHeight: number
) => {
    const img = await loadImage(src);

    const sourceCropX = Math.round((crop.x / Math.max(1, layerWidth)) * img.naturalWidth);
    const sourceCropY = Math.round((crop.y / Math.max(1, layerHeight)) * img.naturalHeight);
    const sourceCropW = Math.round((crop.w / Math.max(1, layerWidth)) * img.naturalWidth);
    const sourceCropH = Math.round((crop.h / Math.max(1, layerHeight)) * img.naturalHeight);

    const safeX = Math.max(0, Math.min(img.naturalWidth - 1, sourceCropX));
    const safeY = Math.max(0, Math.min(img.naturalHeight - 1, sourceCropY));
    const safeW = Math.max(1, Math.min(img.naturalWidth - safeX, sourceCropW));
    const safeH = Math.max(1, Math.min(img.naturalHeight - safeY, sourceCropH));

    const temp = document.createElement("canvas");
    temp.width = crop.w;
    temp.height = crop.h;

    const ctx = temp.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(
        img,
        safeX,
        safeY,
        safeW,
        safeH,
        0,
        0,
        crop.w,
        crop.h
    );

    // eslint-disable-next-line no-restricted-syntax -- interner Crop-Helfer, gibt kein Download-Ergebnis zurueck, Art. 50 KI-VO nicht einschlaegig
    return temp.toDataURL("image/png");
};

export const rectToSelectedLayerCrop = (r: OverlayRect, layer: LayerProps) => {
    const x = Math.round(r.x * layer.width);
    const y = Math.round(r.y * layer.height);
    const w = Math.round(r.w * layer.width);
    const h = Math.round(r.h * layer.height);

    return {
        x: Math.max(0, Math.min(layer.width - 1, x)),
        y: Math.max(0, Math.min(layer.height - 1, y)),
        w: Math.max(1, Math.min(layer.width - x, w)),
        h: Math.max(1, Math.min(layer.height - y, h)),
    };
};

export const getNextLayerMeta = (layers: LayerProps[]) => {
    const nextLayerID =
        layers.length > 0
            ? Math.max(...layers.map((l) => l.layerID)) + 1
            : 1;

    const nextOrderNumber =
        layers.length > 0
            ? String(
                Math.max(
                    ...layers.map((l) => {
                        const parsed = Number(l.orderNumber);
                        return isNaN(parsed) ? 0 : parsed;
                    })
                ) + 1
            )
            : "1";

    return { nextLayerID, nextOrderNumber };
};

export const getLayerBounds = (layer: LayerProps) => ({
    left: layer.position.x,
    top: layer.position.y,
    right: layer.position.x + layer.width,
    bottom: layer.position.y + layer.height
});

export const getLayerCenter = (layer: LayerProps) => ({
    x: layer.position.x + layer.width / 2,
    y: layer.position.y + layer.height / 2
});
export const sortLayersByOrderAscending = (layers: LayerProps[]) => {
    return [...layers].sort(
        (a, b) => Number(a.orderNumber) - Number(b.orderNumber)
    );
};

export const getLayerBoundsUnion = (layers: LayerProps[]) => {
    const bounds = layers.map(getLayerBounds);

    return {
        left: Math.min(...bounds.map((b) => b.left)),
        top: Math.min(...bounds.map((b) => b.top)),
        right: Math.max(...bounds.map((b) => b.right)),
        bottom: Math.max(...bounds.map((b) => b.bottom))
    };
};

export const getHighestOrderNumber = (layers: LayerProps[]) => {
    if (layers.length === 0) return 1;

    return Math.max(
        ...layers.map((l) => {
            const n = Number(l.orderNumber);
            return Number.isFinite(n) ? n : 0;
        })
    );
};

export const getSmallestLayerId = (layers: LayerProps[]) => {
    return Math.min(...layers.map((l) => l.layerID));
};
export const isPointInPolygon = (
    point: { x: number; y: number },
    polygon: { x: number; y: number }[]
) => {
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;

        const intersect =
            yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + Number.EPSILON) + xi;

        if (intersect) inside = !inside;
    }

    return inside;
};
export const isPointInEllipse = (
    point: { x: number; y: number },
    rect: { x: number; y: number; w: number; h: number }
) => {
    const rx = rect.w / 2;
    const ry = rect.h / 2;
    const cx = rect.x + rx;
    const cy = rect.y + ry;

    if (rx <= 0 || ry <= 0) return false;

    const norm =
        ((point.x - cx) * (point.x - cx)) / (rx * rx) +
        ((point.y - cy) * (point.y - cy)) / (ry * ry);

    return norm <= 1;
};
export const hexToRgbaCss = (hex: string, alpha: number) => {
    const clean = hex.replace("#", "");
    const full =
        clean.length === 3
            ? clean.split("").map((c) => c + c).join("")
            : clean;

    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
};
export const hexToRgba = (hex: string, alpha = 1) => {
    const clean = hex.replace("#", "");
    const full =
        clean.length === 3
            ? clean.split("").map((c) => c + c).join("")
            : clean;

    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);

    return [r, g, b, Math.round(alpha * 255)] as const;
};

const colorDistanceWithinTolerance = (
    data: Uint8ClampedArray,
    index: number,
    target: readonly number[],
    tolerance = 36,
    alphaTolerance = 48
) => {
    return (
        Math.abs(data[index] - target[0]) <= tolerance &&
        Math.abs(data[index + 1] - target[1]) <= tolerance &&
        Math.abs(data[index + 2] - target[2]) <= tolerance &&
        Math.abs(data[index + 3] - target[3]) <= alphaTolerance
    );
};

export const applyFloodFill = (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    fillHex: string,
    fillAlpha = 1
) => {
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;

    if (startX < 0 || startY < 0 || startX >= width || startY >= height) return;

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const startIndex = (startY * width + startX) * 4;
    const target = [
        data[startIndex],
        data[startIndex + 1],
        data[startIndex + 2],
        data[startIndex + 3]
    ] as const;

    const replacement = hexToRgba(fillHex, fillAlpha);
    const targetIsTransparent = target[3] <= 40;
    const isFullFill = fillAlpha >= 0.99;

    const sameReplacement =
        Math.abs(target[0] - replacement[0]) <= 2 &&
        Math.abs(target[1] - replacement[1]) <= 2 &&
        Math.abs(target[2] - replacement[2]) <= 2 &&
        Math.abs(target[3] - replacement[3]) <= 2;

    if (sameReplacement) return;

    const visited = new Uint8Array(width * height);
    const filled = new Uint8Array(width * height);
    const stack: [number, number][] = [[startX, startY]];

    const shouldFill = (index: number) => {
        if (targetIsTransparent) {
            return data[index + 3] <= 40;
        }

        return colorDistanceWithinTolerance(data, index, target, 8, 24);
    };

    while (stack.length) {
        const [x, y] = stack.pop()!;
        if (x < 0 || y < 0 || x >= width || y >= height) continue;

        const pixelIndex = y * width + x;
        if (visited[pixelIndex]) continue;
        visited[pixelIndex] = 1;

        const index = pixelIndex * 4;

        if (!shouldFill(index)) continue;

        data[index] = replacement[0];
        data[index + 1] = replacement[1];
        data[index + 2] = replacement[2];
        data[index + 3] = replacement[3];

        filled[pixelIndex] = 1;

        stack.push([x + 1, y]);
        stack.push([x - 1, y]);
        stack.push([x, y + 1]);
        stack.push([x, y - 1]);
    }

    if (targetIsTransparent && isFullFill) {
        const edgePixels: number[] = [];

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const pixelIndex = y * width + x;
                if (filled[pixelIndex]) continue;

                const hasFilledNeighbor =
                    (x > 0 && filled[pixelIndex - 1]) ||
                    (x < width - 1 && filled[pixelIndex + 1]) ||
                    (y > 0 && filled[pixelIndex - width]) ||
                    (y < height - 1 && filled[pixelIndex + width]);

                if (!hasFilledNeighbor) continue;

                const index = pixelIndex * 4;

                if (data[index + 3] < 255) {
                    edgePixels.push(pixelIndex);
                }
            }
        }

        for (const pixelIndex of edgePixels) {
            const index = pixelIndex * 4;
            data[index] = replacement[0];
            data[index + 1] = replacement[1];
            data[index + 2] = replacement[2];
            data[index + 3] = replacement[3];
        }
    }

    ctx.putImageData(imageData, 0, 0);
};
export const wrapCanvasText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] => {
    const paragraphs = text.split("\n");
    const lines: string[] = [];

    paragraphs.forEach((paragraph) => {
        if (!paragraph.trim()) {
            lines.push("");
            return;
        }

        const words = paragraph.split(/\s+/).filter(Boolean);
        let currentLine = "";

        words.forEach((word) => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;

            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
                return;
            }

            if (currentLine) {
                lines.push(currentLine);
                currentLine = word;
                return;
            }

            let chunk = "";
            for (const char of word) {
                const testChunk = chunk + char;
                if (ctx.measureText(testChunk).width <= maxWidth) {
                    chunk = testChunk;
                } else {
                    if (chunk) lines.push(chunk);
                    chunk = char;
                }
            }
            currentLine = chunk;
        });

        lines.push(currentLine);
    });

    return lines;
};
export const renderLayerToCanvas = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    offsetX = 0,
    offsetY = 0
) => {
    if (layer.hidden) return;

    ctx.save();

    const centerX = offsetX + layer.position.x + layer.width / 2;
    const centerY = offsetY + layer.position.y + layer.height / 2;

    ctx.translate(centerX, centerY);

    if (layer.flipHorizontal) ctx.scale(-1, 1);
    if (layer.flipVertical) ctx.scale(1, -1);

    if (layer.rotation) {
        ctx.rotate((layer.rotation * Math.PI) / 180);
    }

    ctx.translate(-layer.width / 2, -layer.height / 2);

    const previewSrc = layer.imageEdit?.src;

    if (previewSrc && (layer.type === "text" || layer.type === "drawings")) {
        const img = await loadImage(previewSrc);
        ctx.drawImage(img, 0, 0, layer.width, layer.height);
        ctx.restore();
        return;
    }

    if (layer.type === "drawings") {
        const rasterSrc = layer.drawingData?.rasterSrc;

        if (rasterSrc) {
            const img = await loadImage(rasterSrc);
            ctx.drawImage(img, 0, 0, layer.width, layer.height);
        }

        ctx.restore();
        return;
    } else if (layer.type === "text" && layer.textData) {
        const {
            text,
            fontColor,
            textOpacity = 1,
            backgroundColor,
            backgroundOpacity = 1,
            borderColor = "#ffffff",
            borderOpacity = 1,
            borderWidth = 0,
            borderStyle = "none",
            fontSize = "16px",
            fontFamily = "Arial",
            style = []
        } = layer.textData;

        const isBold = style.includes("bold");
        const isItalic = style.includes("italic");
        const isUnderline = style.includes("underline");

        const numericFontSize = Number(fontSize.replace("px", "")) || 16;
        const lineHeight = numericFontSize * 1.2;
        const padding = 6;

        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${numericFontSize}px ${fontFamily}`;

        const lines = wrapCanvasText(
            ctx,
            text || "",
            Math.max(1, layer.width - padding * 2)
        );

        if (backgroundColor && backgroundOpacity > 0) {
            ctx.save();
            ctx.globalAlpha = backgroundOpacity;
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, layer.width, layer.height);
            ctx.restore();
        }

        if (borderStyle !== "none" && borderWidth > 0 && borderOpacity > 0) {
            ctx.save();
            ctx.globalAlpha = borderOpacity;
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = borderWidth;

            if (borderStyle === "dashed") ctx.setLineDash([8, 6]);
            else if (borderStyle === "dotted") ctx.setLineDash([2, 4]);
            else ctx.setLineDash([]);

            const inset = borderWidth / 2;
            ctx.strokeRect(inset, inset, layer.width - borderWidth, layer.height - borderWidth);
            ctx.restore();
        }

        ctx.save();
        ctx.globalAlpha = textOpacity;
        ctx.fillStyle = fontColor || "#ffffff";
        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${numericFontSize}px ${fontFamily}`;
        ctx.textBaseline = "top";

        const totalTextHeight = lines.length * lineHeight;
        const startY = Math.max(padding, (layer.height - totalTextHeight) / 2);

        lines.forEach((line, index) => {
            const metricsWidth = ctx.measureText(line).width;
            const x = Math.max(padding, (layer.width - metricsWidth) / 2);
            const y = startY + index * lineHeight;

            if (y + lineHeight > layer.height - padding) return;

            ctx.fillText(line, x, y);

            if (isUnderline && line) {
                const underlineY = y + numericFontSize + 1;
                ctx.beginPath();
                ctx.lineWidth = Math.max(1, numericFontSize / 16);
                ctx.strokeStyle = fontColor || "#ffffff";
                ctx.moveTo(x, underlineY);
                ctx.lineTo(x + metricsWidth, underlineY);
                ctx.stroke();
            }
        });

        ctx.restore();
    } else {
        const src = getLayerRasterSource(layer);
        if (src) {
            const img = await loadImage(src);
            ctx.drawImage(img, 0, 0, layer.width, layer.height);
        }
    }

    ctx.restore();
};
export const mergeLayerAndGetLayerWithSelectionIds = async (layersToMerge: LayerProps[]): Promise<[
        mergedLayer: LayerProps | null,
        selectedLayerIds: Set<number>
]> => {
    if (layersToMerge.length < 2) return [null, new Set([])];

    const sortedSelected = sortLayersByOrderAscending(layersToMerge);
    const union = getLayerBoundsUnion(sortedSelected);

    const mergedWidth = Math.max(1, Math.round(union.right - union.left));
    const mergedHeight = Math.max(1, Math.round(union.bottom - union.top));

    const outCanvas = document.createElement("canvas");
    outCanvas.width = mergedWidth;
    outCanvas.height = mergedHeight;

    const ctx = outCanvas.getContext("2d");
    if (!ctx) return [null, new Set([])];

    for (const layer of sortedSelected) {
        await renderLayerToCanvas(
            ctx,
            {
                ...layer,
                position: {
                    x: layer.position.x - union.left,
                    y: layer.position.y - union.top
                }
            },
            0,
            0
        );
    }

    const mergedFile = await canvasToFile(outCanvas, "merged-layer.png");
    const mergedSrc = URL.createObjectURL(mergedFile);

    const newLayerID = getSmallestLayerId(sortedSelected);
    const highestOrderNumber = String(getHighestOrderNumber(sortedSelected));

    const mergedLayer: LayerProps = {
        type: "image",
        layerID: newLayerID,
        orderNumber: highestOrderNumber,
        position: {
            x: Math.round(union.left),
            y: Math.round(union.top)
        },
        width: mergedWidth,
        height: mergedHeight,
        hidden: false,
        rotation: 0,
        flipHorizontal: false,
        flipVertical: false,
        imageData: {
            file: mergedFile,
            src: mergedSrc,
            width: mergedWidth,
            height: mergedHeight,
            timeOfUse: new Date(),
            title: "merged-layer",
            rotation: 0,
            prompt_positive: "",
            prompt_negative: "",
            colorFilterData: null
        },
        imageEdit: {
            file: mergedFile,
            src: mergedSrc,
            width: mergedWidth,
            height: mergedHeight,
            timeOfUse: new Date(),
            rotation: 0,
            prompt_positive: "",
            prompt_negative: "",
            colorFilterData: null
        }
    };

    const selectedIds = new Set(sortedSelected.map((l) => l.layerID));

    return [mergedLayer, selectedIds];
};
export const  isImageProps = (content: LayerProps | ImageProps): content is ImageProps =>{
    return "src" in content && "file" in content;
};
export const isLayerProps = (content: ImageProps | LayerProps): content is LayerProps => {
    return "layerID" in content && "type" in content;
};
export const createTransparentRaster = (width: number, height: number) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    // eslint-disable-next-line no-restricted-syntax -- erzeugt einen leeren internen Platzhalter-Raster (kein Download), Art. 50 KI-VO nicht einschlaegig
    return canvas.toDataURL("image/png");
};

export const padImageSrcTransparent = async (
    src: string,
    padding: ExtendPadding,
    filename = "extended-image.png"
): Promise<ImageProps> => {
    const img = await loadImage(src);

    const nextWidth = img.width + padding.left + padding.right;
    const nextHeight = img.height + padding.top + padding.bottom;

    const canvas = document.createElement("canvas");
    canvas.width = nextWidth;
    canvas.height = nextHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    ctx.clearRect(0, 0, nextWidth, nextHeight);
    ctx.drawImage(img, padding.left, padding.top);

    const file = await canvasToFile(canvas, filename);
    // eslint-disable-next-line no-restricted-syntax -- internes State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
    const nextSrc = canvas.toDataURL("image/png");

    return {
        file,
        src: nextSrc,
        width: nextWidth,
        height: nextHeight,
        timeOfUse: new Date(),
        title: filename
    };
};
export const padImageSrcTransparentToSize = async (
    src: string,
    padding: ExtendPadding,
    baseWidth: number,
    baseHeight: number,
    filename = "extended-image.png"
): Promise<ImageProps> => {
    const img = await loadImage(src);

    const nextWidth = Math.max(1, Math.round(baseWidth + padding.left + padding.right));
    const nextHeight = Math.max(1, Math.round(baseHeight + padding.top + padding.bottom));

    const canvas = document.createElement("canvas");
    canvas.width = nextWidth;
    canvas.height = nextHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    ctx.clearRect(0, 0, nextWidth, nextHeight);

    ctx.drawImage(
        img,
        0,
        0,
        img.naturalWidth || img.width,
        img.naturalHeight || img.height,
        padding.left,
        padding.top,
        baseWidth,
        baseHeight
    );

    const file = await canvasToFile(canvas, filename);
    // eslint-disable-next-line no-restricted-syntax -- internes State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
    const nextSrc = canvas.toDataURL("image/png");

    return {
        file,
        src: nextSrc,
        width: nextWidth,
        height: nextHeight,
        timeOfUse: new Date(),
        title: filename
    };
};

//display area selection in edit reference
export const drawSelectionPath = (
    ctx: CanvasRenderingContext2D,
    selection: LayerAreaSelection,
    offsetX: number,
    offsetY: number
) => {
    const points = selection.points?.length
        ? selection.points
        : [
            { x: selection.x, y: selection.y },
            { x: selection.x + selection.w, y: selection.y },
            { x: selection.x + selection.w, y: selection.y + selection.h },
            { x: selection.x, y: selection.y + selection.h }
        ];

    ctx.beginPath();

    if (selection.mode === "oval") {
        ctx.ellipse(
            selection.x - offsetX + selection.w / 2,
            selection.y - offsetY + selection.h / 2,
            Math.abs(selection.w / 2),
            Math.abs(selection.h / 2),
            0,
            0,
            Math.PI * 2
        );
        return;
    }

    points.forEach((point, index) => {
        const x = point.x - offsetX;
        const y = point.y - offsetY;

        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    });

    ctx.closePath();
};

export const getLayerCssTransform = (layer: LayerProps) => {
    const transforms: string[] = [];

    if (layer.flipHorizontal) transforms.push("scaleX(-1)");
    if (layer.flipVertical) transforms.push("scaleY(-1)");

    if (typeof layer.rotation === "number" && layer.rotation !== 0) {
        transforms.push(`rotate(${layer.rotation}deg)`);
    }

    return transforms.join(" ");
};

export const getLayerOverlayCounterTransform = (layer: LayerProps) => {
    const transforms: string[] = [];

    if (typeof layer.rotation === "number" && layer.rotation !== 0) {
        transforms.push(`rotate(${-layer.rotation}deg)`);
    }

    if (layer.flipVertical) transforms.push("scaleY(-1)");
    if (layer.flipHorizontal) transforms.push("scaleX(-1)");

    return transforms.join(" ");
};

export const canvasPointToLayerLocalPoint = (point: Point, layer: LayerProps): Point => {
    const rotation = layer.rotation ?? 0;
    const radians = (-rotation * Math.PI) / 180;

    const centerX = layer.position.x + layer.width / 2;
    const centerY = layer.position.y + layer.height / 2;

    let dx = point.x - centerX;
    let dy = point.y - centerY;

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    let rx = dx * cos - dy * sin;
    let ry = dx * sin + dy * cos;

    if (layer.flipHorizontal) rx = -rx;
    if (layer.flipVertical) ry = -ry;

    return {
        x: rx + layer.width / 2,
        y: ry + layer.height / 2
    };
};

export const layerLocalPointToCanvasPoint = (point: Point, layer: LayerProps): Point => {
    const rotation = layer.rotation ?? 0;
    const radians = (rotation * Math.PI) / 180;

    const centerX = layer.position.x + layer.width / 2;
    const centerY = layer.position.y + layer.height / 2;

    let dx = point.x - layer.width / 2;
    let dy = point.y - layer.height / 2;

    if (layer.flipHorizontal) dx = -dx;
    if (layer.flipVertical) dy = -dy;

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return {
        x: centerX + dx * cos - dy * sin,
        y: centerY + dx * sin + dy * cos
    };
};

export const canvasDeltaToLayerLocalDelta = (
    delta: Point,
    layer: LayerProps
): Point => {
    const rotation = layer.rotation ?? 0;
    const radians = (-rotation * Math.PI) / 180;

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    let x = delta.x * cos - delta.y * sin;
    let y = delta.x * sin + delta.y * cos;

    if (layer.flipHorizontal) x = -x;
    if (layer.flipVertical) y = -y;

    return { x, y };
};

export const getVisualPaddingAsLocalPadding = (
    layer: LayerProps,
    padding: ExtendPadding
): ExtendPadding => {
    return visualPaddingToLocalPadding(layer, padding);
};

export const getLayerPositionKeepingLocalPoint = (
    layer: LayerProps,
    nextWidth: number,
    nextHeight: number,
    localPointInNextLayer: Point,
    canvasTargetPoint: Point
) => {
    const rotation = layer.rotation ?? 0;
    const radians = (rotation * Math.PI) / 180;

    let dx = localPointInNextLayer.x - nextWidth / 2;
    let dy = localPointInNextLayer.y - nextHeight / 2;

    if (layer.flipHorizontal) dx = -dx;
    if (layer.flipVertical) dy = -dy;

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const transformedDx = dx * cos - dy * sin;
    const transformedDy = dx * sin + dy * cos;

    return {
        x: Math.round(canvasTargetPoint.x - nextWidth / 2 - transformedDx),
        y: Math.round(canvasTargetPoint.y - nextHeight / 2 - transformedDy)
    };
};